import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Archive,
  Database,
  FileJson,
  Loader2,
  Shield,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Activity,
  Clock,
  Filter,
  History,
  Server,
  Check,
  X,
  ArrowDownToLine,
  Layers,
  RotateCcw,
  AlertTriangle,
  Upload,
  FileArchive,
  Trash2,
  HardDrive,
} from 'lucide-react';

// ─── Interfaces ────────────────────────────────────────────────────────────────

interface EsIndexInfo {
  name: string;
  title: string;
  docsCount: number;
  storeSize: string;
  health: string;
  status: string;
}

interface BackupStatusData {
  mongoConnected: boolean;
  mongoCollections: string[];
  requiredCollections: string[];
  esConnected: boolean;
  esIndices: EsIndexInfo[];
  lastBackup: string | null;
}

interface HistoryEntry {
  _id: string;
  type: 'full' | 'selective';
  status: 'in_progress' | 'success' | 'failed';
  mongoCollections: string[];
  esIndices: string[];
  sizeBytes: number;
  durationMs: number;
  error: string | null;
  createdAt: string;
  createdBy?: { name: string; email: string } | null;
}

type ActiveTab = 'backup' | 'restore' | 'history';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const API_HEADERS = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDuration(ms: number): string {
  if (!ms) return '-';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

// ─── Shared Sub-components ─────────────────────────────────────────────────────

const StatusBadge: React.FC<{ connected: boolean; label: string }> = ({ connected, label }) => (
  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide
    ${connected
      ? 'bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/20'
      : 'bg-red-500/10 text-red-600 ring-1 ring-red-500/20'}`}
  >
    <span className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
    {label}
  </div>
);

const ProgressBar: React.FC<{ percent: number; message: string; variant?: 'emerald' | 'amber' | 'blue' }> = ({
  percent, message, variant = 'emerald'
}) => {
  const colors = {
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    blue: 'from-blue-500 to-indigo-600',
  };
  return (
    <div className="space-y-2 animate-in fade-in">
      <div className="flex justify-between items-center text-sm">
        <span className="text-gray-600 truncate max-w-[70%]">{message}</span>
        <span className="font-bold text-gray-900 tabular-nums">{percent}%</span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${colors[variant]} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const AdminBackupRestorePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('backup');

  // ── Shared state ──
  const [status, setStatus] = useState<BackupStatusData | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ── Backup state ──
  const [backingUp, setBackingUp] = useState(false);
  const [backupPercent, setBackupPercent] = useState(0);
  const [backupProgress, setBackupProgress] = useState('');
  const [backupMode, setBackupMode] = useState<'full' | 'selective'>('full');
  const [selectedCollections, setSelectedCollections] = useState<Set<string>>(new Set());
  const [selectedIndices, setSelectedIndices] = useState<Set<string>>(new Set());

  // ── Restore state ──
  const [restoring, setRestoring] = useState(false);
  const [restorePercent, setRestorePercent] = useState(0);
  const [restoreProgress, setRestoreProgress] = useState('');
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [fileSource, setFileSource] = useState<'upload' | 'server'>('upload');
  const [serverFilePath, setServerFilePath] = useState('');
  const [restoreMode, setRestoreMode] = useState<'full' | 'advanced'>('full');
  const [restoreEsIndices, setRestoreEsIndices] = useState('');
  const [selectedMongoCollections, setSelectedMongoCollections] = useState<Set<string>>(new Set());
  const [restoreSelectedIndices, setRestoreSelectedIndices] = useState<Set<string>>(new Set());
  const [restoreResult, setRestoreResult] = useState<{
    mongoRestored: string[];
    esRestored: string[];
    errors?: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── History state ──
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Data fetching ──

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await fetch('/api/admin/backup/status', { headers: API_HEADERS() });
      if (res.ok) setStatus(await res.json());
    } catch { /* silent */ }
    setStatusLoading(false);
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch('/api/admin/backup/history', { headers: API_HEADERS() });
      if (res.ok) setHistory(await res.json());
    } catch { /* silent */ }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchHistory();
  }, [fetchStatus, fetchHistory]);

  // Ensure required collections are always selected
  useEffect(() => {
    if (!status?.requiredCollections) return;
    setSelectedCollections(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const rc of status.requiredCollections) {
        if (!next.has(rc)) { next.add(rc); changed = true; }
      }
      return changed ? next : prev;
    });
    setSelectedMongoCollections(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const rc of status.requiredCollections) {
        if (!next.has(rc)) { next.add(rc); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [status?.requiredCollections]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // ── Backup handlers ──

  const handleSSEBackup = async (url: string, body?: Record<string, unknown>) => {
    setBackingUp(true);
    clearMessages();
    setBackupPercent(0);
    setBackupProgress('Preparing...');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const fetchOpts: RequestInit = {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      };
      if (body) fetchOpts.body = JSON.stringify(body);

      const response = await fetch(url, fetchOpts);
      if (!response.ok || !response.body) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `Backup failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = JSON.parse(line.slice(6));
          if (data.error) throw new Error(data.error);
          setBackupPercent(data.percent ?? 0);
          setBackupProgress(data.message || '');
          if (data.percent === 100 && data.token) {
            const dlRes = await fetch(`/api/admin/backup/download/${data.token}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!dlRes.ok) throw new Error('Download failed');
            const blob = await dlRes.blob();
            const a = document.createElement('a');
            a.href = window.URL.createObjectURL(blob);
            a.download = `supplychain-backup-${new Date().toISOString().slice(0, 10)}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(a.href);
            document.body.removeChild(a);
            setSuccess('Backup downloaded successfully!');
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Backup failed');
      setBackupPercent(0);
    } finally {
      setBackingUp(false);
      setBackupProgress('');
      fetchHistory();
      fetchStatus();
    }
  };

  const triggerFullBackup = () => handleSSEBackup('/api/admin/backup?progress=1');

  const triggerSelectiveBackup = () => {
    const cols = Array.from(selectedCollections);
    const idxs = Array.from(selectedIndices);
    if (cols.length === 0 && idxs.length === 0) {
      setError('Select at least one collection or index');
      return;
    }
    handleSSEBackup('/api/admin/backup/selective?progress=1', {
      collections: cols.join(','),
      indices: idxs.join(','),
    });
  };

  const toggleCollection = (name: string) => {
    if ((status?.requiredCollections ?? []).includes(name)) return; // can't toggle required
    setSelectedCollections(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleIndex = (name: string) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleAllCollections = () => {
    if (!status) return;
    const required = new Set(status.requiredCollections ?? []);
    const optional = status.mongoCollections.filter(c => !required.has(c));
    // If all optionals are selected, deselect them (keep requireds)
    const allOptionalSelected = optional.every(c => selectedCollections.has(c));
    if (allOptionalSelected) {
      setSelectedCollections(new Set(required));
    } else {
      setSelectedCollections(new Set(status.mongoCollections));
    }
  };

  const toggleAllIndices = () => {
    if (!status) return;
    if (selectedIndices.size === status.esIndices.length)
      setSelectedIndices(new Set());
    else
      setSelectedIndices(new Set(status.esIndices.map(i => i.name)));
  };

  // ── Restore handlers ──

  const toggleRestoreIndex = (name: string) => {
    setRestoreSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleAllRestoreIndices = () => {
    if (!status) return;
    if (restoreSelectedIndices.size === status.esIndices.length) {
      setRestoreSelectedIndices(new Set());
      return;
    }
    setRestoreSelectedIndices(new Set(status.esIndices.map(i => i.name)));
  };

  const toggleRestoreMongoCollection = (name: string) => {
    if ((status?.requiredCollections ?? []).includes(name)) return; // can't toggle required
    setSelectedMongoCollections(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleAllRestoreMongoCollections = () => {
    if (!status) return;
    const required = new Set(status.requiredCollections ?? []);
    const optional = status.mongoCollections.filter(c => !required.has(c));
    const allOptionalSelected = optional.every(c => selectedMongoCollections.has(c));
    if (allOptionalSelected) {
      setSelectedMongoCollections(new Set(required));
    } else {
      setSelectedMongoCollections(new Set(status.mongoCollections));
    }
  };

  const triggerRestore = async (isAdvanced = false) => {
    if (fileSource === 'upload' && !restoreFile) {
      setError('Please select a backup ZIP file');
      return;
    }
    if (fileSource === 'server' && !serverFilePath.trim()) {
      setError('Please enter the backup file path on the server');
      return;
    }

    setRestoring(true);
    clearMessages();
    setRestoreResult(null);
    setRestorePercent(0);
    setRestoreProgress(fileSource === 'server' ? 'Reading backup from server...' : 'Uploading backup...');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      let restoreUrl: string;
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      let body: FormData | string | undefined;

      if (fileSource === 'server') {
        restoreUrl = '/api/admin/restore/server?progress=1';
        const payload: Record<string, string> = { filepath: serverFilePath.trim() };
        if (isAdvanced) {
          const mongoCollections = Array.from(selectedMongoCollections).join(',').trim();
          if (mongoCollections) restoreUrl += `&mongoCollections=${encodeURIComponent(mongoCollections)}`;
          const fromSelection = Array.from(restoreSelectedIndices).join(',').trim();
          const esIndices = (fromSelection || restoreEsIndices).trim();
          if (esIndices) restoreUrl += `&esIndices=${encodeURIComponent(esIndices)}`;
        }
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(payload);
      } else {
        const formData = new FormData();
        formData.append('file', restoreFile!);
        restoreUrl = '/api/admin/restore?progress=1';
        if (isAdvanced) {
          const mongoCollections = Array.from(selectedMongoCollections).join(',').trim();
          if (mongoCollections) {
            restoreUrl += `&mongoCollections=${encodeURIComponent(mongoCollections)}`;
            formData.append('mongoCollections', mongoCollections);
          }
          const fromSelection = Array.from(restoreSelectedIndices).join(',').trim();
          const esIndices = (fromSelection || restoreEsIndices).trim();
          if (esIndices) {
            restoreUrl += `&esIndices=${encodeURIComponent(esIndices)}`;
            formData.append('esIndices', esIndices);
          }
        }
        body = formData;
      }

      const response = await fetch(restoreUrl, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok || !response.body) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `Restore failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = JSON.parse(line.slice(6));
          if (data.error) throw new Error(data.error);
          if (typeof data.percent === 'number') setRestorePercent(data.percent);
          if (typeof data.message === 'string') setRestoreProgress(data.message);
          if (data.percent === 100 && data.success) {
            setSuccess('Restore completed successfully!');
            setRestoreResult(data.data || null);
            setRestoreFile(null);
            setServerFilePath('');
            if (fileInputRef.current) fileInputRef.current.value = '';
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Restore failed');
      setRestorePercent(0);
    } finally {
      setRestoring(false);
      setRestoreProgress('');
      fetchStatus();
    }
  };

  // ── Tab config ──

  const isOperating = backingUp || restoring;

  const tabs: { id: ActiveTab; label: string; icon: React.FC<any> }[] = [
    { id: 'backup', label: 'Backup', icon: Archive },
    { id: 'restore', label: 'Restore', icon: RotateCcw },
    { id: 'history', label: 'History', icon: History },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/20">
              <Archive className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Backup & Restore</h1>
          </div>
          <p className="text-gray-500 text-sm ml-[52px]">Manage MongoDB and Elasticsearch backups and restores</p>
        </div>
        <button
          onClick={() => { fetchStatus(); fetchHistory(); }}
          disabled={statusLoading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${statusLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-emerald-50"><Activity className="h-5 w-5 text-emerald-600" /></div>
            <StatusBadge connected={!statusLoading && !!status} label={statusLoading ? 'Checking...' : 'Operational'} />
          </div>
          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">System</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5">
            {statusLoading ? '...' : status ? 'Protected' : 'Unavailable'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-green-50"><Database className="h-5 w-5 text-green-600" /></div>
            <StatusBadge connected={status?.mongoConnected ?? false} label={status?.mongoConnected ? 'Connected' : 'Offline'} />
          </div>
          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">MongoDB</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5">
            {status ? `${status.mongoCollections.length} collections` : '...'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-blue-50"><Server className="h-5 w-5 text-blue-600" /></div>
            <StatusBadge connected={status?.esConnected ?? false} label={status?.esConnected ? 'Connected' : 'Offline'} />
          </div>
          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">Elasticsearch</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5">
            {status ? `${status.esIndices.length} indices` : '...'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 rounded-lg bg-violet-50"><Clock className="h-5 w-5 text-violet-600" /></div>
            {status?.lastBackup && (
              <span className="text-xs text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full font-medium ring-1 ring-violet-500/20">
                {timeAgo(status.lastBackup)}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 uppercase tracking-wider font-medium">Last Backup</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5">
            {status?.lastBackup ? new Date(status.lastBackup).toLocaleDateString() : 'Never'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => { setActiveTab(id); if (id === 'history') fetchHistory(); }}
              className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 text-sm font-semibold transition-all relative
                ${activeTab === id
                  ? 'text-emerald-700 bg-emerald-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            >
              <Icon className="h-4 w-4" />
              {label}
              {activeTab === id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500" />
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Messages */}
          {(error || success) && (
            <div className="mb-6 space-y-3">
              {error && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Error</p>
                    <p className="text-sm text-red-600 mt-0.5">{error}</p>
                  </div>
                  <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                </div>
              )}
              {success && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Success</p>
                    <p className="text-sm text-emerald-600 mt-0.5">{success}</p>
                  </div>
                  <button onClick={() => setSuccess(null)} className="ml-auto text-emerald-400 hover:text-emerald-600"><X className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          )}

          {/* ================= BACKUP TAB ================= */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              {/* Mode Toggle */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
                <button
                  onClick={() => setBackupMode('full')}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    backupMode === 'full'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-2"><Layers className="h-4 w-4" /> Full Backup</span>
                </button>
                <button
                  onClick={() => setBackupMode('selective')}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    backupMode === 'selective'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span className="flex items-center gap-2"><Filter className="h-4 w-4" /> Selective</span>
                </button>
              </div>

              {backupMode === 'full' ? (
                /* Full Backup */
                <div className="space-y-5">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100">
                    <p className="text-sm text-emerald-800">
                      <strong>Full backup</strong> exports all MongoDB collections and Elasticsearch indices into a single ZIP file. Elasticsearch uses the Scroll API for unlimited document export.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-gray-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Database className="h-4 w-4 text-green-600" />
                        <h3 className="text-sm font-semibold text-gray-800">MongoDB Collections</h3>
                        <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {status?.mongoCollections.length ?? 0}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                        {status?.mongoCollections.map(c => (
                          <span key={c} className="px-2.5 py-1 bg-green-50 text-green-700 text-xs rounded-lg font-medium border border-green-100">{c}</span>
                        ))}
                        {(!status || status.mongoCollections.length === 0) && (
                          <span className="text-xs text-gray-400 italic">No collections found</span>
                        )}
                      </div>
                    </div>
                    <div className="border border-gray-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <FileJson className="h-4 w-4 text-blue-600" />
                        <h3 className="text-sm font-semibold text-gray-800">Elasticsearch Indices</h3>
                        <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {status?.esIndices.length ?? 0}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                        {status?.esIndices.map(i => (
                          <span key={i.name} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg font-medium border border-blue-100">
                            {i.title || i.name}
                            <span className="text-blue-400 ml-1">({i.docsCount.toLocaleString()})</span>
                          </span>
                        ))}
                        {(!status || status.esIndices.length === 0) && (
                          <span className="text-xs text-gray-400 italic">No indices found</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {backingUp && <ProgressBar percent={backupPercent} message={backupProgress} variant="emerald" />}

                  <button
                    onClick={triggerFullBackup}
                    disabled={isOperating}
                    className="flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30"
                  >
                    {backingUp ? (
                      <><Loader2 className="h-5 w-5 animate-spin" />Creating Backup...</>
                    ) : (
                      <><ArrowDownToLine className="h-5 w-5" />Download Full Backup (ZIP)</>
                    )}
                  </button>
                </div>
              ) : (
                /* Selective Backup */
                <div className="space-y-5">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100">
                    <p className="text-sm text-violet-800">
                      <strong>Selective backup</strong> lets you choose specific collections and indices to include. Only selected items will be exported.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* MongoDB Selectable */}
                    <div className="border border-gray-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Database className="h-4 w-4 text-green-600" />
                          <h3 className="text-sm font-semibold text-gray-800">MongoDB</h3>
                        </div>
                        <button
                          onClick={toggleAllCollections}
                          className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                        >
                          {status && selectedCollections.size === status.mongoCollections.length ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                        {status?.mongoCollections.map(c => {
                          const isRequired = (status.requiredCollections ?? []).includes(c);
                          return (
                          <label
                            key={c}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                              ${isRequired ? 'bg-amber-50 border border-amber-200 cursor-default' : selectedCollections.has(c) ? 'bg-green-50 border border-green-200 cursor-pointer' : 'hover:bg-gray-50 border border-transparent cursor-pointer'}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCollections.has(c)}
                              onChange={() => toggleCollection(c)}
                              disabled={isRequired}
                              className={`h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 ${isRequired ? 'opacity-60' : ''}`}
                            />
                            <span className={`text-sm ${isRequired ? 'text-amber-800 font-medium' : 'text-gray-700'}`}>{c}</span>
                            {isRequired && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold uppercase tracking-wide">Required</span>}
                          </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* ES Selectable */}
                    <div className="border border-gray-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FileJson className="h-4 w-4 text-blue-600" />
                          <h3 className="text-sm font-semibold text-gray-800">Elasticsearch</h3>
                        </div>
                        <button
                          onClick={toggleAllIndices}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {status && selectedIndices.size === status.esIndices.length ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                        {status?.esIndices.map(i => (
                          <label
                            key={i.name}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors
                              ${selectedIndices.has(i.name) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIndices.has(i.name)}
                              onChange={() => toggleIndex(i.name)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 flex-1">{i.title || i.name}</span>
                            <span className="text-xs text-gray-400">{i.docsCount.toLocaleString()} docs</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {backingUp && <ProgressBar percent={backupPercent} message={backupProgress} variant="blue" />}

                  <div className="flex items-center gap-4">
                    <button
                      onClick={triggerSelectiveBackup}
                      disabled={isOperating || (selectedCollections.size === 0 && selectedIndices.size === 0)}
                      className="flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-xl hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-violet-500/20"
                    >
                      {backingUp ? (
                        <><Loader2 className="h-5 w-5 animate-spin" />Creating Selective Backup...</>
                      ) : (
                        <><Filter className="h-5 w-5" />Backup Selected ({selectedCollections.size + selectedIndices.size})</>
                      )}
                    </button>
                    <span className="text-xs text-gray-400">
                      {selectedCollections.size} collection{selectedCollections.size !== 1 ? 's' : ''}, {selectedIndices.size} index{selectedIndices.size !== 1 ? 'es' : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ================= RESTORE TAB ================= */}
          {activeTab === 'restore' && (
            <div className="space-y-6">
              {/* Mode Toggle */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
                <button
                  onClick={() => setRestoreMode('full')}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    restoreMode === 'full' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Full Restore
                </button>
                <button
                  onClick={() => setRestoreMode('advanced')}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    restoreMode === 'advanced' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Advanced Restore
                </button>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  This action replaces existing data and is irreversible. Use only trusted backup files.
                </p>
              </div>

              {/* File Source */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Backup Source</label>
                <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
                  <button
                    type="button"
                    onClick={() => { setFileSource('upload'); setServerFilePath(''); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      fileSource === 'upload' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Upload className="h-4 w-4" />
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => { setFileSource('server'); setRestoreFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      fileSource === 'server' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <HardDrive className="h-4 w-4" />
                    From Server
                  </button>
                </div>
              </div>

              {fileSource === 'upload' && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Backup File (.zip)</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative flex items-center justify-center gap-3 p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                      restoreFile ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".zip"
                      className="hidden"
                      onChange={(e) => {
                        setRestoreFile(e.target.files?.[0] || null);
                        setError(null);
                      }}
                    />
                    {restoreFile ? (
                      <div className="flex items-center gap-3">
                        <FileArchive className="h-8 w-8 text-emerald-600" />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{restoreFile.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{formatBytes(restoreFile.size)}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRestoreFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="ml-4 p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-600">Click to upload a backup file</p>
                        <p className="text-xs text-gray-400 mt-1">Only .zip files are accepted</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {fileSource === 'server' && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Server File Path</label>
                  <div className="flex items-center gap-2">
                    <HardDrive className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={serverFilePath}
                      onChange={(e) => { setServerFilePath(e.target.value); setError(null); }}
                      placeholder="/path/to/backup.zip or backup-filename.zip"
                      className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent placeholder:text-gray-400"
                      disabled={restoring}
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    Enter the absolute path to a .zip file on the server, or just a filename to use the default backups directory.
                  </p>
                </div>
              )}

              {/* Advanced Restore Options */}
              {restoreMode === 'advanced' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700">MongoDB Collections Selection</label>
                      <button
                        type="button"
                        onClick={toggleAllRestoreMongoCollections}
                        className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                      >
                        {status && selectedMongoCollections.size === status.mongoCollections.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-xl p-2 space-y-1">
                      {status?.mongoCollections?.length ? status.mongoCollections.map((col) => {
                        const isRequired = (status.requiredCollections ?? []).includes(col);
                        return (
                        <label
                          key={col}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                            isRequired
                              ? 'bg-amber-50 border border-amber-200 cursor-default'
                              : selectedMongoCollections.has(col)
                                ? 'bg-emerald-50 border border-emerald-200 cursor-pointer'
                                : 'hover:bg-gray-50 border border-transparent cursor-pointer'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedMongoCollections.has(col)}
                            onChange={() => toggleRestoreMongoCollection(col)}
                            className={`h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 ${isRequired ? 'opacity-60' : ''}`}
                            disabled={restoring || isRequired}
                          />
                          <span className={`text-sm flex-1 ${isRequired ? 'text-amber-800 font-medium' : 'text-gray-700'}`}>{col}</span>
                          {isRequired && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold uppercase tracking-wide">Required</span>}
                        </label>
                        );
                      }) : (
                        <p className="text-sm text-gray-400 p-2">No collection list available.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700">Elasticsearch Indices Selection</label>
                      <button
                        type="button"
                        onClick={toggleAllRestoreIndices}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {status && restoreSelectedIndices.size === status.esIndices.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-xl p-2 space-y-1">
                      {status?.esIndices?.length ? status.esIndices.map((idx) => (
                        <label
                          key={idx.name}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer ${
                            restoreSelectedIndices.has(idx.name)
                              ? 'bg-blue-50 border border-blue-200'
                              : 'hover:bg-gray-50 border border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={restoreSelectedIndices.has(idx.name)}
                            onChange={() => toggleRestoreIndex(idx.name)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            disabled={restoring}
                          />
                          <span className="text-sm text-gray-700 flex-1">{idx.title || idx.name}</span>
                          <span className="text-xs text-gray-400">{idx.docsCount.toLocaleString()} docs</span>
                        </label>
                      )) : (
                        <p className="text-sm text-gray-400 p-2">No index list available.</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Manual Indices Filter (optional)</label>
                    <input
                      type="text"
                      value={restoreEsIndices}
                      onChange={(e) => setRestoreEsIndices(e.target.value)}
                      placeholder="idx_users, idx_projects, idx_events"
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent placeholder:text-gray-400"
                      disabled={restoring}
                    />
                    <p className="text-xs text-gray-400">Used only if no checkboxes are selected.</p>
                  </div>
                </div>
              )}

              {/* Progress */}
              {restoring && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{restoreProgress}</span>
                    <span className="font-semibold text-amber-700">{restorePercent}%</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
                      style={{ width: `${restorePercent}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Result */}
              {restoreResult && (
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 space-y-2">
                  <p className="text-sm font-semibold text-emerald-800">Restore Summary</p>
                  {restoreResult.mongoRestored?.length > 0 && (
                    <p className="text-xs text-emerald-700">
                      <Database className="h-3 w-3 inline mr-1" />
                      MongoDB: {restoreResult.mongoRestored.join(', ')}
                    </p>
                  )}
                  {restoreResult.esRestored?.length > 0 && (
                    <p className="text-xs text-emerald-700">
                      <FileJson className="h-3 w-3 inline mr-1" />
                      Elasticsearch: {restoreResult.esRestored.join(', ')}
                    </p>
                  )}
                  {restoreResult.errors && restoreResult.errors.length > 0 && (
                    <p className="text-xs text-amber-700">Warnings: {restoreResult.errors.join('; ')}</p>
                  )}
                </div>
              )}

              <button
                onClick={() => triggerRestore(restoreMode === 'advanced')}
                disabled={restoring || (fileSource === 'upload' ? !restoreFile : !serverFilePath.trim())}
                className="flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-amber-500/20"
              >
                {restoring ? (
                  <><Loader2 className="h-5 w-5 animate-spin" />Restoring...</>
                ) : (
                  <><RotateCcw className="h-5 w-5" />{restoreMode === 'full' ? 'Restore Full Backup' : 'Restore Advanced'}</>
                )}
              </button>
            </div>
          )}

          {/* ================= HISTORY TAB ================= */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Recent Backups</h3>
                <button
                  onClick={fetchHistory}
                  disabled={historyLoading}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <RefreshCw className={`h-3 w-3 ${historyLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {historyLoading && history.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  Loading history...
                </div>
              ) : history.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <History className="h-10 w-10 mb-3 opacity-50" />
                  <p className="text-sm font-medium">No backup history</p>
                  <p className="text-xs mt-1">Create your first backup to see it here</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/80">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Items</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Size</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Duration</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {history.map((h) => (
                        <tr key={h._id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-900">{new Date(h.createdAt).toLocaleDateString()}</p>
                            <p className="text-xs text-gray-400">{new Date(h.createdAt).toLocaleTimeString()}</p>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold
                              ${h.type === 'full'
                                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/20'
                                : 'bg-violet-50 text-violet-700 ring-1 ring-violet-500/20'}`}
                            >
                              {h.type === 'full' ? <Layers className="h-3 w-3" /> : <Filter className="h-3 w-3" />}
                              {h.type === 'full' ? 'Full' : 'Selective'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {h.mongoCollections?.slice(0, 3).map(c => (
                                <span key={c} className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded">{c}</span>
                              ))}
                              {h.esIndices?.slice(0, 3).map(i => {
                                const title = status?.esIndices.find(ei => ei.name === i)?.title || i;
                                return <span key={i} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{title}</span>;
                              })}
                              {((h.mongoCollections?.length || 0) + (h.esIndices?.length || 0)) > 6 && (
                                <span className="text-xs text-gray-400">+{(h.mongoCollections?.length || 0) + (h.esIndices?.length || 0) - 6} more</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600 font-medium tabular-nums">
                            {h.sizeBytes ? formatBytes(h.sizeBytes) : '-'}
                          </td>
                          <td className="py-3 px-4 text-gray-600 tabular-nums">
                            {formatDuration(h.durationMs)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold
                              ${h.status === 'success'
                                ? 'bg-emerald-50 text-emerald-700'
                                : h.status === 'failed'
                                  ? 'bg-red-50 text-red-600'
                                  : 'bg-amber-50 text-amber-700'}`}
                            >
                              {h.status === 'success' && <Check className="h-3 w-3" />}
                              {h.status === 'failed' && <X className="h-3 w-3" />}
                              {h.status === 'in_progress' && <Loader2 className="h-3 w-3 animate-spin" />}
                              {h.status === 'success' ? 'Success' : h.status === 'failed' ? 'Failed' : 'Running'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Security Footer */}
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-100">
        <Shield className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Security Notice</p>
          <p className="text-sm text-gray-500 mt-1">
            Backups contain sensitive data including user information and application state. Store backup files securely and restrict access to authorized personnel only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminBackupRestorePage;